import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { fetchCourse, fetchInterventions, loc } from "@/lib/physicsApi";
import { useLessonProgress } from "@/lib/academyProgress";
import { EmeraldCosmos, PhysicsImage } from "./PhysicsImage";
import { TermText, usePhysicsTerms } from "./PhysicsTermChip";
import { useSEO } from "@/lib/useSEO";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { LockedLesson } from "@/components/academy/TrialGate";
import { LessonTopNav, LessonBottomNav, CompletionPanel } from "@/components/academy/LessonNav";
import { BTN_FX, DISCIPLINE_THEME, tpl } from "@/lib/academyTheme";

const MENTOR_NAMES = { marcus: "Marcus Aurelius", epictetus: "Epictetus", seneca: "Seneca", musonius: "Musonius Rufus", hipparchia: "Hipparchia" };

export default function PhysicsLesson() {
  const { phaseSlug, lessonSlug } = useParams();
  const { lang, t } = useT();
  const L = (v) => loc(v, lang);
  const th = DISCIPLINE_THEME.physics;
  const [course, setCourse] = useState(null);
  const [shortNote, setShortNote] = useState(false);
  const [interventions, setInterventions] = useState(null);
  const terms = usePhysicsTerms();
  const {
    responses, exerciseResults, setResponse, setExerciseResult,
    flush, complete, saveState, completed, lastSavedAt,
  } = useLessonProgress("physics", lessonSlug);

  useEffect(() => {
    setShortNote(false);
    fetchCourse().then(setCourse).catch(() => {});
    fetchInterventions().then(setInterventions).catch(() => {});
  }, [lessonSlug]);

  const phase = course?.phases?.find((p) => p.slug === phaseSlug);
  const lesson = phase?.lessons?.find((l) => l.slug === lessonSlug);

  useSEO({
    title: lesson?.seo?.title || (lesson ? `${loc(lesson.title)} | Stoic Physics | StoicOs.ai Academy` : "Stoic Physics"),
    description: lesson?.seo?.description || (lesson ? loc(lesson.subtitle) : ""),
    canonical: `https://stoicos.ai/academy/physics/phase/${phaseSlug}/lesson/${lessonSlug}`,
    jsonLd: lesson ? [{ "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Stoic Physics", item: "https://stoicos.ai/academy/physics" },
        { "@type": "ListItem", position: 2, name: loc(phase.title), item: `https://stoicos.ai/academy/physics/phase/${phaseSlug}` },
        { "@type": "ListItem", position: 3, name: loc(lesson.title), item: `https://stoicos.ai/academy/physics/phase/${phaseSlug}/lesson/${lessonSlug}` },
      ] }] : [],
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  if (course.access === "preview" || (course.access === "intro" && lesson?.locked)) return <LockedLesson discipline="physics" backTo="/academy/physics" mode={course.access} />;
  if (!phase || !lesson) return <Navigate to="/academy/physics" replace />;
  const media = course.media || {};
  const allLessons = course.phases.flatMap((p) => p.lessons.map((l) => ({ ...l, phaseSlug: p.slug })));
  const idx = allLessons.findIndex((l) => l.slug === lessonSlug);
  const prev = allLessons[idx - 1];
  const next = allLessons[idx + 1];
  const minChars = lesson.exercise?.completionRule?.minChars || 30;
  const exFields = lesson.exercise?.fields || [];
  const exKey = (i) => `exercise:${i}`;
  const exVal = (i) => exerciseResults[exKey(i)] || "";
  const exComplete = exFields.length > 0 && exFields.every((_, i) => exVal(i).trim().length >= minChars);
  const anyStarted = exFields.some((_, i) => exVal(i).trim().length > 0);
  const shortNode = interventions?.interventions?.short_response?.[lesson.mentor];
  const completeNode = interventions?.interventions?.exercise_complete?.[lesson.mentor];
  const journal = responses.journalPrompt || "";

  const onExerciseChange = (i, value) => {
    const complete_ = exFields.every((_, j) => ((j === i ? value : exVal(j)) || "").trim().length >= minChars);
    if (complete_) setShortNote(false);
    setExerciseResult(exKey(i), value);
  };

  const d = lastSavedAt ? new Date(lastSavedAt) : null;
  const savedLine =
    saveState === "saving" ? t("academy.save.saving")
    : saveState === "failed" ? t("academy.save.failed")
    : saveState === "saved" ? t("academy.save.saved")
    : d ? tpl(t("academy.save.lastSaved"), {
        date: d.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB"),
        time: d.toLocaleTimeString(lang === "nl" ? "nl-NL" : "en-GB", { hour: "2-digit", minute: "2-digit" }),
      }) : "";

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-20" data-testid="physics-lesson-page">
      <div className="max-w-3xl mx-auto space-y-14">
        <header>
          <DisciplineSwitcher active="physics" />
          <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-[#7fb69a] mb-6">
            <Link to="/academy/physics" className="hover:text-gold">Stoic Physics</Link> / <Link to={`/academy/physics/phase/${phase.slug}`} className="hover:text-gold">{L(phase.title)}</Link>
          </nav>
          <LessonTopNav discipline="physics" phase={phase} lesson={lesson} prev={prev} next={next} total={allLessons.length} locTitle={L} />
          <div className="flex items-center gap-3 mb-4">
            <EmeraldCosmos size={28} />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a]">{lang === "nl" ? "Fase" : "Phase"} {phase.order} · {lang === "nl" ? "Les" : "Lesson"} {lesson.order} · {lesson.estimatedMinutes} min</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-bone">{L(lesson.title)}</h1>
          <p className="mt-5 font-serif italic text-lg text-gold/90">{L(lesson.subtitle)}</p>
          <ul className="mt-4 space-y-1" data-testid="physics-lesson-objectives">
            {lesson.learningObjectives?.map((o, i) => (
              <li key={i} className="text-sm text-fog/75 font-reading flex items-baseline gap-2">
                <span className="h-px w-3 shrink-0 bg-[#7fb69a]/60 translate-y-[-3px]" aria-hidden />{L(o)}
              </li>
            ))}
          </ul>
        </header>

        <section aria-labelledby="lesson-historical">
          <h2 id="lesson-historical" className="text-xs tracking-[0.3em] uppercase text-[#7fb69a] mb-3">{lang === "nl" ? "Historische Kern" : "Historical Core"}</h2>
          <p className="font-reading text-fog leading-[1.95]"><TermText terms={terms} text={L(lesson.historicalCore)} /></p>
          <p className="mt-3 inline-block text-[10px] tracking-[0.18em] uppercase text-gold/70 border border-gold/25 px-2 py-1" data-testid="physics-historical-status">
            {L(lesson.historicalStatusLabel)}
          </p>
        </section>

        <section className="border-l-2 border-gold/50 pl-6 py-2">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-3">{lang === "nl" ? "Moderne Vertaling" : "Modern Translation"}</p>
          <p className="font-serif text-xl text-bone leading-relaxed"><TermText terms={terms} text={L(lesson.modernTranslation)} /></p>
        </section>

        <section aria-labelledby="lesson-scenario">
          <h2 id="lesson-scenario" className="text-xs tracking-[0.3em] uppercase text-[#7fb69a] mb-4">{lang === "nl" ? "De Situatie" : "The Situation"}</h2>
          {lesson.imageSlotId && <div className="mb-6 max-w-lg"><PhysicsImage media={media} assetKey={lesson.imageSlotId} alt="" /></div>}
          <p className="font-reading text-fog leading-[1.95] text-base" data-testid="physics-lesson-scenario"><TermText terms={terms} text={L(lesson.scenario)} /></p>
        </section>

        <section aria-labelledby="lesson-exercise">
          <h2 id="lesson-exercise" className="text-xs tracking-[0.3em] uppercase text-[#7fb69a] mb-3">{lang === "nl" ? "De Oefening" : "The Exercise"}</h2>
          <p className="text-sm text-fog/85 font-reading mb-5">{L(lesson.exercise?.instruction)}</p>
          <div className="space-y-5">
            {exFields.map((f, i) => (
              <div key={f.id} className="border border-[#c8a95a]/15 bg-[#14211a] p-4">
                <label htmlFor={`ex-${f.id}`} className="block text-sm text-bone/90 font-reading mb-2">{L(f.label)}</label>
                <textarea id={`ex-${f.id}`} rows={3} value={exVal(i)}
                  data-testid={`physics-exercise-${f.id}`}
                  onChange={(e) => onExerciseChange(i, e.target.value)}
                  onBlur={() => setShortNote(anyStarted && !exComplete)}
                  className="w-full bg-transparent border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
                  placeholder={lang === "nl" ? "Schrijf vrijuit. Je eerste versie is materiaal, geen fout." : "Write freely. Your first version is material, not a mistake."} />
              </div>
            ))}
          </div>
          {shortNote && shortNode && (
            <div className="mt-3 border-l-2 border-[#7fb69a]/60 bg-[#182620]/70 pl-4 pr-3 py-3" data-testid="physics-mentor-short-response">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#7fb69a]">{MENTOR_NAMES[lesson.mentor]}</p>
              <p className="mt-1.5 text-sm text-fog/95 font-reading leading-relaxed">{L(shortNode)}</p>
            </div>
          )}
          {exComplete && completeNode && (
            <p className="mt-5 border-l-2 border-gold/50 pl-4 py-1 font-serif italic text-bone/90" data-testid="physics-mentor-complete">
              <span className="not-italic text-[10px] tracking-[0.25em] uppercase text-[#7fb69a] block mb-1">{MENTOR_NAMES[lesson.mentor]}</span>
              {L(completeNode)}
            </p>
          )}
        </section>

        <section className="border border-[#7fb69a]/25 bg-[#182620] p-6" aria-label="Mentor intervention" data-testid="physics-mentor-intervention">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a] mb-2">{MENTOR_NAMES[lesson.mentor]} · {lang === "nl" ? "mentorstem, parafrase" : "mentor voice, paraphrase"}</p>
          <p className="font-serif italic text-lg text-bone/95"><TermText terms={terms} text={L(lesson.mentorIntervention)} /></p>
        </section>

        <section aria-labelledby="lesson-journal">
          <h2 id="lesson-journal" className="text-xs tracking-[0.3em] uppercase text-[#7fb69a] mb-3">{lang === "nl" ? "Journalvraag · bewaard in je account" : "Journal Question · saved to your account"}</h2>
          <p className="text-sm text-bone/90 font-reading italic mb-4" data-testid="physics-journal-prompt">{L(lesson.journalPrompt)}</p>
          <textarea data-testid="physics-journal-input" value={journal} rows={5} aria-label="Your journal answer"
            onChange={(e) => setResponse("journalPrompt", e.target.value)}
            className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-4 focus:outline-none focus:ring-2 focus:ring-gold/50" />
          <div className="flex items-center justify-between mt-1">
            <p aria-live="polite" data-testid="physics-save-status" className={`text-[11px] h-4 ${saveState === "failed" ? "text-red-300" : "text-gold/70"}`}>{savedLine}</p>
            <Link to={`/academy/physics/record?fromPhase=${phase.slug}&fromLesson=${lesson.slug}`} className="text-[10px] tracking-[0.2em] uppercase text-[#bfdccb] hover:text-gold">{lang === "nl" ? "Open het volledige Record of Reality →" : "Open the full Record of Reality →"}</Link>
          </div>
        </section>

        <section className="border border-[#c8a95a]/30 bg-[#f3e9d2]/[0.06] p-6" aria-labelledby="lesson-mastery">
          <h2 id="lesson-mastery" className="text-xs tracking-[0.3em] uppercase text-gold/80 mb-3">{lang === "nl" ? "Beheersingsbewijs" : "Mastery Evidence"}</h2>
          <p className="font-reading text-fog leading-[1.9] text-sm" data-testid="physics-mastery-evidence">{L(lesson.masteryEvidence)}</p>
          <p className="mt-3 text-[11px] text-fog/60">Demonstrate this in the <Link to="/academy/physics/path" className="text-gold hover:underline">Path of Nature</Link> with a real example.</p>
        </section>

        <section className="border-t border-white/10 pt-6 space-y-4" aria-label="Save actions">
          <div className="flex flex-wrap gap-3">
            <button data-testid="physics-save-draft" onClick={() => flush()}
              className="min-h-[48px] px-6 border border-[#7fb69a]/50 text-[#bfdccb] text-[11px] tracking-[0.2em] uppercase hover:bg-[#2e5040]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7fb69a]">
              {t("academy.save.draftBtn")}
            </button>
            <button data-testid="physics-save-complete" onClick={() => complete()} disabled={completed}
              className={`min-h-[48px] px-6 text-[11px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
              {t("academy.save.completeBtn")}
            </button>
          </div>
          {completed && (
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#7fb69a]" data-testid="physics-lesson-completed-note">
              ✓ {t("academy.save.completedNote")}
            </p>
          )}
          {(saveState === "saved" || completed) && (
            <p className="text-[11px] tracking-[0.18em] uppercase">
              <Link to="/academy/my-work" className="text-[#bfdccb] hover:text-gold" data-testid="physics-lesson-mywork-link">{t("academy.nav.myWork")} →</Link>
            </p>
          )}
        </section>

        {completed && <CompletionPanel discipline="physics" returnCtx={{ lesson: { ...lesson, phaseSlug: phase.slug }, prev, next, phaseSlug: phase.slug }} locTitle={L} />}

        <LessonBottomNav discipline="physics" phase={phase} lesson={{ ...lesson, phaseSlug: phase.slug }} prev={prev} next={next} locTitle={L} />
      </div>
    </main>
  );
}
